<script lang="ts" setup>
// ─── IMPORTS ────────────────────────────────────────────────────────────────
import { Company } from './models/company'
import { ref, onMounted, computed } from 'vue'
import { useUserStore } from './store'

// ─── STORES ────────────────────────────────────────────────────────────────
const userStore = useUserStore()

// ─── REFS: UI Flags and Data ───────────────────────────────────────────────
const loading = ref(false)
const activeEditCompany = ref(false)
const activeCreateNewCompany = ref(false)
const loadingCreateCompany = ref(false)
const loadingEditCompany = ref(false)
const searchCompanies = ref('')
const page = ref(1)
const itemsPerPage = 10
const formTouched = ref(false)
const formRef = ref()
const formValid = ref(false)

const headers = [
  { title: 'CÓDIGO', key: 'code' },
  { title: 'NOMBRE', key: 'name' },
  { title: 'NOMBRE EXTENDIDO', key: 'extendedName' },
  { title: 'CREADO EN', key: 'createdAt' },
  { title: 'ACCIONES', key: 'actions' },
]

// ─── REFS: Company ──────────────────────────────────────────────────────────
const companies = ref<Company[]>([])
const company = ref<Company>(new Company())

// ─── ALERTAS ───────────────────────────────────────────────────────────────
const activeAlert = ref(false)
const textAlert = ref('')
const colorAlert = ref<'error' | 'success' | 'info' | 'warning'>('error')

// ─── VALIDACIONES ──────────────────────────────────────────────────────────
const codeError = ref('')
const nameError = ref('')

// ─── COMPUTED ──────────────────────────────────────────────────────────────
const pageCount = computed(() => Math.ceil(companies.value.length / itemsPerPage))

// ─── FUNCIONES AUXILIARES ──────────────────────────────────────────────────
const resetForm = () => {
  company.value = new Company()
  codeError.value = ''
  nameError.value = ''
  formTouched.value = false
}

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

// ─── EVENTOS / BOTONES ─────────────────────────────────────────────────────
const getCompanies = async () => {
  loading.value = true
  try {
    companies.value = await userStore.getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al obtener las empresas'
    colorAlert.value = 'error'
  }
  loading.value = false
}

const btnSetActiveCreateCompany = () => {
  activeCreateNewCompany.value = !activeCreateNewCompany.value
  company.value = new Company()
  codeError.value = ''
  nameError.value = ''
}

const validateForm = (): boolean => {
  let isValid = true

  // Validación de campos obligatorios
  if (!company.value.code) {
    codeError.value = 'Código requerido'
    isValid = false
  } else {
    codeError.value = ''
  }

  if (!company.value.name) {
    nameError.value = 'Nombre requerido'
    isValid = false
  } else {
    nameError.value = ''
  }

  if (!isValid) {
    activeAlert.value = true
    textAlert.value = 'Todos los campos obligatorios deben ser completados'
    colorAlert.value = 'error'
  }

  return isValid
}

const btnSetActiveEditCompany = async (selectedCompany: Company) => {
  company.value = new Company(selectedCompany)
  activeEditCompany.value = true
}

const btnCreateCompany = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  loadingCreateCompany.value = true
  
  try {
    await userStore.postCompany(company.value)
    activeAlert.value = true
    textAlert.value = 'Empresa creada exitosamente'
    colorAlert.value = 'success'
    activeCreateNewCompany.value = false
    getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al crear la empresa'
    colorAlert.value = 'error'
  }
  
  loadingCreateCompany.value = false
}

const btnUpdateCompany = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  loadingEditCompany.value = true
  
  try {
    await userStore.putCompany(company.value)
    activeAlert.value = true
    textAlert.value = 'Empresa actualizada exitosamente'
    colorAlert.value = 'success'
    activeEditCompany.value = false
    getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al actualizar la empresa'
    colorAlert.value = 'error'
  }
  
  loadingEditCompany.value = false
}

const btnDeleteCompany = async (companyToDelete: Company) => {
  if (confirm(`¿Está seguro de eliminar la empresa "${companyToDelete.name}"?`)) {
    try {
      await userStore.deleteCompany(companyToDelete.id)
      activeAlert.value = true
      textAlert.value = 'Empresa eliminada exitosamente'
      colorAlert.value = 'success'
      getCompanies()
    } catch (error) {
      activeAlert.value = true
      textAlert.value = 'Error al eliminar la empresa'
      colorAlert.value = 'error'
    }
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────────────────────────
onMounted(async () => {
  // Cargar empresas desde el store
  await getCompanies()
})
</script>

<template>
  <v-card elevation="0" class="bg-background pa-5 rounded-lg">
    <!-- Alertas -->
    <v-alert v-model="activeAlert" :type="colorAlert" dismissible class="mb-4">
      {{ textAlert }}
    </v-alert>

    <!-- Vista principal de empresas -->
    <div v-if="!activeCreateNewCompany && !activeEditCompany">
      <v-card class="mt-3 rounded-lg" elevation="3">
        <div class="rounded-lg justify-space-between mb-4 pa-2 d-flex">
          <v-card-title color="primary" class="text-subtitle-4">Empresas</v-card-title>
          <v-spacer />
          <v-btn 
            v-if="!activeCreateNewCompany && !activeEditCompany" 
            @click="btnSetActiveCreateCompany" 
            variant="text" 
            text="Nueva empresa"
          />
          <v-btn 
            v-else 
            @click="(activeCreateNewCompany = false), (activeEditCompany = false)" 
            variant="text"
            text="Volver a las empresas"
          />
        </div>
        
        <v-text-field 
          v-model="searchCompanies" 
          append-inner-icon="mdi-magnify" 
          density="compact" 
          label="Buscar empresa" 
          variant="solo" 
          hide-details 
          single-line 
          color="surface-light" 
          class="mx-4" 
          bg-color="background"
        />
        
        <v-data-table 
          class="rounded-lg text-body-2" 
          v-model:page="page" 
          :headers="headers"
          :items="companies" 
          :search="searchCompanies" 
          :loading="loading" 
          :items-per-page="itemsPerPage"
        >
          <template v-slot:loading>
            <v-skeleton-loader class="bg-background" type="table-row@10" />
          </template>
          
          <template #[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="36" class="mr-3" color="primary">
                <span class="text-white text-subtitle-2 font-weight-bold">
                  {{ item.code?.substring(0, 2).toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.code }}</div>
              </div>
            </div>
          </template>
          
          <template #[`item.extendedName`]="{ item }">
            <div class="text-body-2">
              {{ item.extendedName || 'N/A' }}
            </div>
          </template>
          
          <template #[`item.createdAt`]="{ item }">
            <div class="text-body-2">
              {{ formatDate(item.createdAt) }}
            </div>
          </template>
          
          <template #[`item.actions`]="{ item }">
            <div class="d-flex gap-1">
              <v-btn 
                variant="text" 
                @click="btnSetActiveEditCompany(item)" 
                icon="mdi-pencil-outline"
                v-tooltip="`Editar`"
              />
              <v-btn 
                variant="text" 
                @click="btnDeleteCompany(item)" 
                icon="mdi-delete-outline"
                color="error"
                v-tooltip="`Eliminar`"
              />
            </div>
          </template>
          
          <template v-slot:bottom>
            <div class="text-center pt-2">
              <v-pagination v-model="page" :length="pageCount" />
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Formulario de crear/editar empresa -->
    <v-row v-if="activeCreateNewCompany || activeEditCompany" justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card elevation="4" class="bg-background pa-0 ma-0 rounded-lg content-container">
          <!-- Sticky Action Bar -->
          <div class="sticky-header bg-grey-lighten-4 d-flex justify-space-between align-center px-4 py-3" 
               style="border-bottom: 1px solid #e0e0e0;">
            <span class="text-h6 font-weight-medium">
              {{ activeCreateNewCompany ? 'Crear nueva empresa' : 'Editar empresa' }}
            </span>
            <div>
              <v-btn 
                color="red" 
                variant="tonal" 
                class="mr-2" 
                rounded="lg" 
                @click="resetForm"
              >
                Limpiar
              </v-btn>
              <v-btn 
                color="primary" 
                variant="tonal" 
                class="mr-2" 
                rounded="lg"
                @click="activeCreateNewCompany = false, activeEditCompany = false"
              >
                Volver
              </v-btn>
              <v-btn 
                color="primary" 
                class="text-white" 
                rounded="lg" 
                :loading="loadingCreateCompany || loadingEditCompany"
                @click="activeCreateNewCompany ? btnCreateCompany() : btnUpdateCompany()"
              >
                {{ activeCreateNewCompany ? 'Crear' : 'Actualizar' }}
              </v-btn>
            </div>
          </div>

          <!-- Main Content -->
          <v-row justify="center">
            <v-col cols="12">
              <v-sheet color="white" class="pa-6" elevation="0">
                <v-form ref="formRef" v-model="formValid" validate-on="submit lazy">
                  <div class="pa-6">
                    <h4 class="text-h6 mb-6">Información de la empresa</h4>

                    <v-row>
                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field 
                          v-model="company.code" 
                          label="Código *"
                          :error-messages="formTouched && codeError ? [codeError] : []" 
                          variant="outlined"
                          density="comfortable" 
                          bg-color="white"
                          hint="Código único de la empresa"
                          persistent-hint
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field 
                          v-model="company.name" 
                          label="Nombre *"
                          :error-messages="formTouched && nameError ? [nameError] : []" 
                          variant="outlined"
                          density="comfortable" 
                          bg-color="white"
                          hint="Nombre de la empresa"
                          persistent-hint
                        />
                      </v-col>
                      
                      <v-col cols="12" class="mb-3">
                        <v-text-field 
                          v-model="company.extendedName" 
                          label="Nombre extendido"
                          variant="outlined"
                          density="comfortable" 
                          bg-color="white"
                          hint="Nombre completo o razón social (opcional)"
                          persistent-hint
                        />
                      </v-col>
                    </v-row>

                    <div class="text-caption text-medium-emphasis mt-4">
                      * Campos obligatorios
                    </div>
                  </div>
                </v-form>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
}

.content-container {
  max-height: 80vh;
  overflow-y: auto;
}
</style>
